/**
 * The header's search (review #403, Shayna 2026-10-12: "also add search so can look for
 * anything", pointing at a SEARCH box in another app whose hint reads "visit, patient,
 * facility, …").
 *
 * One box over everything a reader looks things up by: people (by name or Paylocity ID,
 * #373), supervisors, facilities, facility groups, payer types, departments, job
 * titles, states and the pages themselves. Each hit says where it opens; the component
 * (components/GlobalSearch.tsx) does the opening.
 *
 * And a claim, by its visit id or claim id (#604, Shayna 2026-10-14: "can we add
 * workflow visit id to the smart search?"): "4252996", "273-4252996" or "visit
 * 4252996". Exact, not "contains": a visit id is a number, and "425" would match
 * thousands. Read off Claim detail's rows, which /api/claims cuts to the caller.
 *
 * It searches the loaded bundle and nothing else, and the bundle is already cut to the
 * caller's access by the API, so a reader can never find someone outside it. No request
 * leaves the browser and nothing typed is kept.
 *
 * Matching is ui/match.ts's, words in any order, as every filter dropdown matches. Hits
 * are ranked: the label starting with the query, then a word of it starting with a
 * query word, then anywhere. Groups follow their best hit, so "facil" puts the
 * Facilities page above a facility whose name merely contains it.
 */
import { matches, words } from './match'

export type SearchKind =
  | 'person'
  | 'supervisor'
  | 'facility'
  | 'facilityGroup'
  | 'payer'
  | 'department'
  | 'jobTitle'
  | 'state'
  | 'page'
  | 'claim'

export interface SearchItem {
  kind: SearchKind
  /** What the hit opens: a name, a facility, a page path… */
  value: string
  label: string
  /** Searched with the label and shown faint after it: a Paylocity ID, a state code. */
  detail?: string | null
  /** Shown under the label, not searched: a person's job title and department. */
  sub?: string | null
}

/** Each kind's heading, in the order groups take when their hits rank level. */
export const KIND_LABEL: Record<SearchKind, string> = {
  person: 'People',
  supervisor: 'Supervisors',
  facility: 'Facilities',
  facilityGroup: 'Facility groups',
  payer: 'Payer types',
  department: 'Departments',
  jobTitle: 'Job titles',
  state: 'States',
  page: 'Pages',
  claim: 'Claims',
}

/** Where a hit of each kind opens — the faint note at its right. */
export const KIND_OPENS: Record<SearchKind, string> = {
  person: 'Opens their figures',
  supervisor: 'Opens their team',
  facility: 'Opens on Facilities',
  facilityGroup: 'Opens on Facilities',
  payer: 'Narrows the Overview',
  department: 'Filters every page',
  jobTitle: 'Filters every page',
  state: 'Narrows the Overview',
  page: 'Goes to the page',
  claim: 'Opens Claim detail',
}

const KIND_ORDER = Object.keys(KIND_LABEL) as SearchKind[]

/** How many hits a group shows before "and N more". */
export const PER_GROUP = 6

export interface SearchGroup {
  kind: SearchKind
  items: SearchItem[]
  /** Hits in the group beyond `items`. */
  more: number
}

/** 0 = the label starts with the query, 1 = a word starts with each query word, 2 = anywhere. */
function rank(item: SearchItem, query: string): number {
  const l = item.label.toLowerCase()
  const q = query.trim().toLowerCase()
  if (l.startsWith(q) || (item.detail ?? '').toLowerCase().startsWith(q)) return 0
  const ws = words(`${item.label} ${item.detail ?? ''}`)
  return words(q).every((w) => ws.some((x) => x.startsWith(w))) ? 1 : 2
}

/** The hits for `query`, grouped by kind, best group first. Nothing for an empty query. */
export function searchAll(items: readonly SearchItem[], query: string, perGroup = PER_GROUP): SearchGroup[] {
  if (!words(query).length) return []
  const byKind = new Map<SearchKind, { item: SearchItem; r: number }[]>()
  for (const item of items) {
    if (!matches(item.detail ? `${item.label} ${item.detail}` : item.label, query)) continue
    const list = byKind.get(item.kind) ?? []
    list.push({ item, r: rank(item, query) })
    byKind.set(item.kind, list)
  }
  const groups = [...byKind.entries()].map(([kind, hits]) => {
    hits.sort((a, b) => a.r - b.r || a.item.label.localeCompare(b.item.label))
    return { kind, best: hits[0]!.r, hits }
  })
  groups.sort((a, b) => a.best - b.best || KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind))
  return groups.map((g) => ({
    kind: g.kind,
    items: g.hits.slice(0, perGroup).map((h) => h.item),
    more: Math.max(0, g.hits.length - perGroup),
  }))
}

/**
 * The id a query names, when it reads as a claim: "4252996", "273-4252996", "visit
 * 4252996", "claim #273-4252996". Null for anything else, so typing a name never loads
 * the claim rows.
 */
export function claimQuery(query: string): string | null {
  const m = /^\s*(?:(?:workflow\s+)?(?:visit|claim)(?:\s*id)?\s*[#:]?\s*)?(\d{3,}(?:-\d{3,})?)\s*$/i.exec(query)
  return m ? m[1]! : null
}

/**
 * The rows of a claim, by `id` from claimQuery. A row's visit id (`v`) is Medevolve's
 * VisitID; its claim id is `<facility id>-<visit id>` (`fid`; build_bundle.py). So
 * "4252996" finds the visit, and "273-4252996" finds visit 4252996 at facility 273 —
 * or a visit id written with its prefix, either way round.
 */
export function findClaimRows(
  v: readonly string[],
  fid: readonly (number | null)[] | undefined,
  id: string,
): number[] {
  const dash = id.indexOf('-')
  const pre = dash > 0 ? Number(id.slice(0, dash)) : null
  const post = dash > 0 ? id.slice(dash + 1) : null
  const tail = `-${id}`
  const out: number[] = []
  for (let i = 0; i < v.length; i++) {
    const x = v[i]!
    if (x === id) out.push(i)
    else if (post !== null ? x === post && (fid?.[i] ?? pre) === pre : x.endsWith(tail)) out.push(i)
  }
  return out
}
